/**
 * Spec 004 — member-register fetch wrappers. Same discriminated-union contract as lib/auth.ts: these
 * NEVER throw; each returns an explicit outcome the UI renders across the four states. All requests send
 * cookies; mutations echo the double-submit CSRF token.
 */
import { readCsrfToken, type FieldErrors } from './auth';

const TIMEOUT_MS = 10_000;

export type { FieldErrors };

// ---- shared shapes ----

export interface Paginated<T> {
  items: T[];
  page: number;
  pageSize: number;
  total: number;
}

export interface ApplicationRow {
  id: string;
  fullName: string;
  email: string;
  message: string | null;
  howHeard: string | null;
  createdAt: string;
}

export interface MemberRow {
  id: string;
  displayName: string;
  email: string;
  role: 'member' | 'board' | 'admin';
  status: 'active' | 'disabled';
  joinedAt: string;
}

export interface MemberDetail extends MemberRow {
  accountId: string;
  pronouns: string | null;
  bio: string | null;
  consentVersion: string;
}

export interface OwnProfile {
  id: string;
  displayName: string;
  pronouns: string | null;
  bio: string | null;
  joinedAt: string;
  role: 'member' | 'board' | 'admin';
  status: 'active' | 'disabled';
  notificationOptIn: boolean;
  consentVersion: string;
  consentAt: string;
}

async function request(
  path: string,
  method: string,
  body?: unknown,
  opts: { csrf?: boolean } = {},
): Promise<Response> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), TIMEOUT_MS);
  const headers: Record<string, string> = { accept: 'application/json' };
  if (body !== undefined) headers['content-type'] = 'application/json';
  if (opts.csrf) {
    const token = readCsrfToken();
    if (token) headers['x-csrf-token'] = token;
  }
  try {
    return await fetch(path, {
      method,
      credentials: 'include',
      headers,
      body: body === undefined ? undefined : JSON.stringify(body),
      signal: controller.signal,
    });
  } finally {
    clearTimeout(timer);
  }
}

/** Read a `{ error: { code, message } }` (or legacy `{ message }`) body without throwing. */
async function readError(res: Response): Promise<{ code?: string; message?: string }> {
  const body = (await res.json().catch(() => ({}))) as
    | { error?: { code?: string; message?: string }; message?: string; code?: string }
    | undefined;
  if (body?.error) return body.error;
  return { code: body?.code, message: body?.message };
}

// ---- generic result unions ----

export type ListResult<T> = { kind: 'ok'; data: Paginated<T> } | { kind: 'forbidden' } | { kind: 'error' };
export type ActionResult<T = void> =
  | { kind: 'ok'; data: T }
  | { kind: 'conflict'; code: string; message: string }
  | { kind: 'invalid'; errors: FieldErrors; message?: string }
  | { kind: 'forbidden' }
  | { kind: 'not_found'; message: string }
  | { kind: 'error' };

async function toAction<T>(res: Response): Promise<ActionResult<T>> {
  if (res.ok) return { kind: 'ok', data: (await res.json().catch(() => ({}))) as T };
  if (res.status === 403) return { kind: 'forbidden' };
  if (res.status === 404) {
    const e = await readError(res);
    return { kind: 'not_found', message: e.message ?? 'Not found.' };
  }
  if (res.status === 409) {
    const e = await readError(res);
    return { kind: 'conflict', code: e.code ?? 'conflict', message: e.message ?? 'That action conflicts with the current state.' };
  }
  if (res.status === 400) {
    const body = (await res.json().catch(() => ({}))) as { errors?: FieldErrors; error?: { message?: string }; message?: string };
    return { kind: 'invalid', errors: body.errors ?? {}, message: body.error?.message ?? body.message };
  }
  return { kind: 'error' };
}

async function toList<T>(res: Response): Promise<ListResult<T>> {
  if (res.ok) return { kind: 'ok', data: (await res.json()) as Paginated<T> };
  if (res.status === 403) return { kind: 'forbidden' };
  return { kind: 'error' };
}

// ---- approval queue (board+) ----

export async function listApplications(page = 1): Promise<ListResult<ApplicationRow>> {
  try {
    return toList<ApplicationRow>(await request(`/api/applications?status=pending&page=${page}`, 'GET'));
  } catch {
    return { kind: 'error' };
  }
}

export async function approveApplication(id: string): Promise<ActionResult<{ member: { displayName: string }; emailSent: boolean }>> {
  try {
    return toAction(await request(`/api/applications/${id}/approve`, 'POST', {}, { csrf: true }));
  } catch {
    return { kind: 'error' };
  }
}

export async function rejectApplication(id: string, reason?: string): Promise<ActionResult> {
  try {
    return toAction(await request(`/api/applications/${id}/reject`, 'POST', { reason }, { csrf: true }));
  } catch {
    return { kind: 'error' };
  }
}

export async function resendWelcome(memberId: string): Promise<ActionResult<{ emailSent: boolean }>> {
  try {
    return toAction(await request(`/api/members/${memberId}/resend-welcome`, 'POST', {}, { csrf: true }));
  } catch {
    return { kind: 'error' };
  }
}

// ---- member register (board+) ----

export async function listMembers(params: { page?: number; role?: string; status?: string; q?: string }): Promise<ListResult<MemberRow>> {
  const qs = new URLSearchParams();
  qs.set('page', String(params.page ?? 1));
  if (params.role) qs.set('role', params.role);
  if (params.status) qs.set('status', params.status);
  if (params.q) qs.set('q', params.q);
  try {
    return toList<MemberRow>(await request(`/api/members?${qs.toString()}`, 'GET'));
  } catch {
    return { kind: 'error' };
  }
}

export async function getMember(id: string): Promise<ActionResult<MemberDetail>> {
  try {
    return toAction<MemberDetail>(await request(`/api/members/${id}`, 'GET'));
  } catch {
    return { kind: 'error' };
  }
}

// ---- governance (admin) ----

export async function changeRole(accountId: string, role: string): Promise<ActionResult<{ role: string }>> {
  try {
    return toAction(await request(`/api/accounts/${accountId}/role`, 'PUT', { role }, { csrf: true }));
  } catch {
    return { kind: 'error' };
  }
}

export async function changeStatus(accountId: string, status: 'active' | 'disabled'): Promise<ActionResult<{ status: string }>> {
  try {
    return toAction(await request(`/api/accounts/${accountId}/status`, 'PUT', { status }, { csrf: true }));
  } catch {
    return { kind: 'error' };
  }
}

// ---- own profile + GDPR (member) ----

export async function getOwnProfile(): Promise<ActionResult<OwnProfile>> {
  try {
    return toAction<OwnProfile>(await request('/api/members/me', 'GET'));
  } catch {
    return { kind: 'error' };
  }
}

export async function updateOwnProfile(body: {
  displayName: string;
  pronouns?: string;
  bio?: string;
  notificationOptIn: boolean;
}): Promise<ActionResult<OwnProfile>> {
  try {
    return toAction<OwnProfile>(await request('/api/members/me', 'PATCH', body, { csrf: true }));
  } catch {
    return { kind: 'error' };
  }
}

/** Trigger a browser download of the caller's own data export. Returns ok/forbidden/error. */
export async function exportOwnData(): Promise<{ kind: 'ok' } | { kind: 'error' }> {
  try {
    const res = await request('/api/members/me/export', 'GET');
    if (!res.ok) return { kind: 'error' };
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `my-data-${new Date().toISOString().slice(0, 10)}.json`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    return { kind: 'ok' };
  } catch {
    return { kind: 'error' };
  }
}

export async function eraseSelf(confirm: string): Promise<ActionResult> {
  try {
    return toAction(await request('/api/members/me/erase', 'POST', { confirm }, { csrf: true }));
  } catch {
    return { kind: 'error' };
  }
}

export async function eraseMember(memberId: string, confirm: string): Promise<ActionResult> {
  try {
    return toAction(await request(`/api/members/${memberId}/erase`, 'POST', { confirm }, { csrf: true }));
  } catch {
    return { kind: 'error' };
  }
}
